// Generic interface for PageResponse

export interface SEOInterface {
  id: number;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  metaRobots: string;
  structuredData: any;
  canonicalURL: string;
  metaImage: any;
  metaSocial: any;
}

export interface PageResponse<T> {
  data: {
    id: number;
    attributes: {
      createdAt: string;
      updatedAt: string;
      publishedAt: string;
      seo?: SEOInterface;
    } & T;
  };
  meta: any;
}

// VideoSlider Component
export interface VideoSliderComponent {
  id: number;
  __component: string;
  ComponentType: "VideoSlider";
  SliderList: VideoSliderItem[];
}

// Slider item for VideoSlider
export interface VideoSliderItem {
  id: number;
  Title: string;
  Video: { data: MediaData };
  CTAText?: string;
  titleVariant?: "light" | "dark";
}

// OnboardingSection Component
export interface OnboardingSectionComponent {
  id: number;
  __component: string;
  ComponentType: "OnboardingSection";
  Title: string;
  Description: string;
  Journey: JourneyItem[];
}

// JourneyItem for OnboardingSection
export interface JourneyItem {
  id: number;
  Title: string;
  Type: `${HOME_TYPE}`;
}

// Discover Component
export interface DiscoverComponent {
  id: number;
  __component: string;
  ComponentType: "Discover";
  Title: string;
  Description: string;
  Categories: Category[];
}

// JourneyItem for Discover
export interface Category {
  Id: number;
  CategoryName: string;
  ShortDescription: string;
  Slug: string;
  ImageUrl: string | null;
}

// CareTeam Component
export interface CareTeamComponent {
  id: number;
  __component: string;
  ComponentType: "CareTeam";
  Title: string;
  NumberText: CareTeamInfoItem[];
  Icon: {
    data: MediaData;
  };
  Image: {
    data: MediaData;
  };
}

export interface CareTeamInfoItem {
  id: number;
  Number: string;
  Text: string;
}

// ToolsWidget Component
export interface ToolsWidgetComponent {
  id: number;
  __component: string;
  ComponentType: "ToolsWidget";
  Title: string;
  Description: string;
  Tools: {
    data: Tool[];
  };
}

// Tool for ToolsWidget
export interface Tool {
  id: number;
  attributes: {
    Name: string;
    Title: string;
    CTAUrl: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    Image: {
      data: MediaData;
    };
  };
}

// QuickReads Component
export interface QuickReadsComponent {
  id: number;
  __component: string;
  ComponentType: "QuickReads";
  Title: string;
  Description: string;
  Categories: {
    data: QuickReadCategory[];
  };
  articles: Article[];
}

export interface Article {
  id: number;
  publishedAt: string;
  Slug: string;
  CategoryName?: string;
  createdAt: string;
  updatedAt: string;
  ImgUrl?: string;
  Title: string;
  Description: string;
  PostedOn: string;
  AuthorName: string;
  AuthorDesignation: string;
  ReadTime: string;
  Tags?: any;
  Image?: {
    data: MediaData;
  };
}

// QuickReadCategory for QuickReads
export interface QuickReadCategory {
  id: number;
  attributes: {
    CategoryName: string;
    ShortDescription: string;
    displayOnNavBar: boolean;
    Slug: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    CategoryImage?: {
      data: MediaData;
    };
  };
}

// DoctorRecommendation Component
export interface DoctorRecommendationComponent {
  id: number;
  __component: string;
  ComponentType: "DoctorRecommendation";
  Title: string;
  Description: string;
  ApiUrl: string;
}

export interface ShopEssentialsItem {
  id: number;
  attributes: {
    CategoryName: string;
    ShortDescription: string;
    displayOnNavBar: boolean;
    Slug: string;
    ShoppingDesc: string;
    ShopApiUrl: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    CategoryImage?: {
      data: MediaData;
    };
    ShoppingImage?: {
      data: MediaData;
    };
  };
}

export interface ShopEssentials {
  id: number;
  __component: string;
  ComponentType: "ShopByCatWid";
  Title: string;
  Description: string;
  Categories: {
    data: ShopEssentialsItem[];
  };
}

// TrendingReads Component
export interface TrendingReadsComponent {
  id: number;
  __component: string;
  ComponentType: "TrendingReads";
  Title: string;
  Description: string;
  Categories: {
    data: QuickReadCategory[];
  };
}

export interface BannerInfo {
  id: number;
  __component: string;
  ComponentType: "AyurvedicCare";
  Title: string;
  Description: string;
  CTAText: string;
  CTAUrl: string;
  ImageOpacity: number;
  Image: {
    data: MediaData;
  };
}

export interface ArticleByTagBase {
  id: number;
  __component: string;
  ComponentType: "ArticlesByTag" | "DynamicArticles";
  Title: string;
  Tag?: TagItem;
}

export interface ArticleByTag extends ArticleByTagBase {
  articles?: DynamicArticleItem[];
}

export interface TagItem {
  data: {
    id: number;
    attributes: {
      TagName: string;
      ShortDescription: string;
      createdAt: string;
      updatedAt: string;
      publishedAt: string;
    };
  };
}

export interface DynamicArticleItem {
  id: number;
  Slug: string;
  Title: string;
  Description: string;
  AuthorName: string;
  ReadTime: string;
  ImgUrl: string;
  CategoryName: string;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  Image: {
    data: MediaData;
  };
}

// Media formats for images
export interface MediaFormat {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string | null;
  size: number;
  width: number;
  height: number;
  sizeInBytes: number;
}

// Image data structure
export interface MediaData {
  id: number;
  attributes: {
    name: string;
    alternativeText: string | null;
    caption: string | null;
    width: number;
    height: number;
    formats: {
      large: MediaFormat;
      small: MediaFormat;
      medium: MediaFormat;
      thumbnail: MediaFormat;
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string | null;
    provider: string;
    provider_metadata: any | null;
    createdAt: string;
    updatedAt: string;
  };
}

export type HomeContentItem =
  | NavBarContent
  | VideoSliderComponent
  | OnboardingSectionComponent
  | DiscoverComponent
  | CareTeamComponent
  | ToolsWidgetComponent
  | QuickReadsComponent
  | DoctorRecommendationComponent
  | TrendingReadsComponent;

type HomeContentType = {
  HomeContent: HomeContentItem[];
};

export enum HOME_TYPE {
  NORMAL = "Home",
  PRECONCEPTION = "PreconceptionHome",
  PREGNANCY = "PregnancyHome",
  BIRTH = "BirthHome",
  NEW_MOM = "MotherHome",
}

export interface onBoardUserAttr {
  type: `${HOME_TYPE}`;
  dueDate?: string;
  firstDateOfLastPeriod?: string;
  conceptionDateBasedOnScan?: string;
  averageCycle?: number;
  childBirthDate?: string;
}

export type HomePageResponse = PageResponse<HomeContentType>;

export interface SegmentInfo extends onBoardUserAttr {
  segmentDetail: FirstFoldPreconception | FirstFoldPregnancy | FirstFoldNewMom;
}

// Base Response Interface
interface BaseResponse {
  id: number;
  Title: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  segmentInfo: SegmentInfo;
  message?: string; // This key comes on error
}

// Common Component Base Interface
interface ComponentBase {
  __component: string;
  id: number;
  ComponentType: string;
}

// Shared Substructures
export interface NavBarListItem {
  id: number;
  Name: string;
  Url: string;
  Sequence: number;
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  publishedAt: string; // ISO 8601 date string
}

// NavBar Component
export interface NavBarContent extends ComponentBase {
  __component: "pers-home-master.nav-bar";
  ComponentType: "NavBar";
  navBarList: NavBarListItem[];
}

// FirstFold Base Component
interface FirstFoldBase extends ComponentBase {
  __component: "pers-home-master.first-fold";
  ComponentType: "FirstFold";
  firstFoldImage: string;
}

interface FertileWindow {
  start: string; // ISO 8601 date string
  end: string; // ISO 8601 date string
}

// Specialized FirstFold Components
export interface FirstFoldPreconception extends FirstFoldBase {
  averageCycleDays: string;
  averageCycle: string;
  firstDateOfLastPeriod: string; // ISO 8601 date string
  ovulationDate: string; // ISO 8601 date string
  fertileWindow: FertileWindow;
}

export interface FirstFoldPregnancy extends FirstFoldBase {
  currentWeek: number;
  currentTrimester: number;
  remainingWeeks: number;
  dueDate: string; // ISO 8601 date string
}

export interface FirstFoldNewMom extends FirstFoldBase {
  Birthday: string;
  childAge: string;
  nutrition: string;
  sleep: string;
  socialSkills: string;
  MotorSkills: string;
}

// ExpertContent Component
interface ExpertContent extends ComponentBase {
  __component: "pers-home-master.expert-content";
  ComponentType: "ExpertContent";
  Title: string; // HTML string
  Description: string; // HTML string
  articles: any[]; // Define more specific types if available
}

export interface ClusterItem {
  id: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  ClusterNumber: string;
  ClusterType: string;
  Name: string;
  Slug: string;
}

// DynamicClusters Component
export interface DynamicClusters extends ComponentBase {
  __component: "cluster-type.dynamic-clusters";
  ComponentType: "DynamicClusters";
  Title: string; // HTML string
  Description: string; // HTML string
  clusters: ClusterItem[];
}

// Shared Content Item Union Types
export type PreconceptionContentItem = NavBarContent | FirstFoldPreconception;
export type PregnancyContentItem = NavBarContent | FirstFoldPregnancy | ExpertContent | DynamicClusters;
export type BirthContentItem = NavBarContent | FirstFoldPregnancy | ExpertContent;

// Root Response Interfaces
export interface PreconceptionHomeResponse extends BaseResponse {
  PreconceptionContent: PreconceptionContentItem[];
}

export interface PregnancyHomeResponse extends BaseResponse {
  PregnancyContent: PregnancyContentItem[];
}

export interface BirthHomeResponse extends BaseResponse {
  BirthContent: BirthContentItem[];
}

export type HomePageType = HomePageResponse | PreconceptionHomeResponse | BirthHomeResponse | PregnancyHomeResponse;
